import React, { createContext, useContext, useState, useEffect } from 'react';
import { getDomainByHostname } from '@/config/domains';

interface DomainConfig {
  name: string;
  logo: string;
  primaryColor: string;
  seoTitle: string;
  seoDescription: string;
}

export interface DomainContextType {
  currentDomain: string;
  domainConfig: DomainConfig;
  setDomainFromHostname: (hostname: string) => void;
}

// Generic default config for fallback
const defaultConfig: DomainConfig = {
  name: 'Health News',
  logo: '/logo.png',
  primaryColor: '#2563eb',
  seoTitle: 'Health News - Latest Articles and Updates',
  seoDescription: 'Stay informed with the latest health news, treatments, and expert advice.',
};

const DomainContext = createContext<DomainContextType>({
  currentDomain: '',
  domainConfig: defaultConfig,
  setDomainFromHostname: () => {},
});

export const useDomain = () => useContext(DomainContext);

export const DomainProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentDomain, setCurrentDomain] = useState('');
  const [domainConfig, setDomainConfig] = useState<DomainConfig>(defaultConfig);

  const setDomainFromHostname = (hostname: string) => {
    // Remove www. if present
    const cleanHostname = hostname.replace(/^www\./, '');
    setCurrentDomain(cleanHostname);

    // Get domain configuration from domains.ts
    const domain = getDomainByHostname(cleanHostname);
    if (domain) {
      setDomainConfig({
        name: domain.name,
        logo: domain.logo_path,
        primaryColor: domain.theme.primaryColor,
        seoTitle: domain.seo.title,
        seoDescription: domain.seo.description,
      });
    } else {
      setDomainConfig(defaultConfig);
    }
  };

  useEffect(() => {
    // Set initial domain from current hostname
    setDomainFromHostname(window.location.hostname);
  }, []);

  return (
    <DomainContext.Provider value={{ currentDomain, domainConfig, setDomainFromHostname }}>
      {children}
    </DomainContext.Provider>
  );
}; 